import React, { useMemo } from 'react';
import { Icon } from '@iconify/react';
import type { Interaction, ISRNote, MappedLeadData } from '../../types';
import { STATUS_MAP, type StatusType } from '../../utils/statusMapping';

/**
 * ActivityTimeline — read-only notes + interactions timeline.
 *
 * The same merged view the ISR workspace's Activity rail renders (manual
 * notes, system status changes, and channel interactions, newest first) but
 * without any editing affordances. Reused by read-only surfaces like the
 * lead details modal's Interactions tab.
 */

interface TimelineItem {
  id: string;
  kind: 'note' | 'status' | 'magnet' | 'interaction';
  timestamp: string;
  title: string;
  body: string;
  author: string;
  /** HUMAN (ISR wrote a note), AI AGENT (system interaction), SYSTEM (status change or magnet sent). */
  actor: 'HUMAN' | 'AI AGENT' | 'SYSTEM';
  icon: string;
  statusTone?: { color: string; background: string; border: string };
}

const ACTOR_BADGE: Record<TimelineItem['actor'], { icon: string; color: string; bg: string; border: string }> = {
  HUMAN: { icon: 'bx:user', color: 'text-cyan-200', bg: 'bg-cyan-400/10', border: 'border-cyan-300/20' },
  'AI AGENT': { icon: 'solar:magic-wand-3-bold-duotone', color: 'text-violet-200', bg: 'bg-violet-400/10', border: 'border-violet-300/20' },
  SYSTEM: { icon: 'bx:bot', color: 'text-white/50', bg: 'bg-white/[0.04]', border: 'border-white/10' },
};

/** Mirrors the ISR rail's status-change parsing so titles match. */
const statusActivityPresentation = (note: ISRNote): { title: string; body: string; status: StatusType | null } => {
  let title = note.title || 'Status changed';
  let body = note.body;
  let statusOption = findSalesOption(title);

  const statusChangeMatch = body.match(/^Status changed from (.+?) to (.+?)\.?$/i);
  if (statusChangeMatch) {
    const [previousStatus] = statusChangeMatch[1].split(/\s+·\s+/);
    const [nextStatus, nextSubstatus] = statusChangeMatch[2].replace(/\.$/, '').split(/\s+·\s+/);
    statusOption = findSalesOption(nextStatus) || statusOption;
    title = nextStatus;
    body = `${previousStatus} → ${title}${nextSubstatus ? ` | ${nextSubstatus}` : ''}`;
  } else {
    const substatusChangeMatch = body.match(/^(.+?) moved from (.+?) to (.+?)\.?$/i);
    if (substatusChangeMatch) {
      statusOption = findSalesOption(substatusChangeMatch[1]) || statusOption;
      title = substatusChangeMatch[1];
      body = `${title} | ${substatusChangeMatch[2]} → ${substatusChangeMatch[3].replace(/\.$/, '')}`;
    }
  }

  const key = (Object.keys(STATUS_MAP) as StatusType[]).find((candidate) => STATUS_MAP[candidate].label === title);
  return { title, body, status: key ?? statusOption ?? null };
};

const findSalesOption = (label: string): StatusType | null => {
  const normalized = (label || '').trim().toLowerCase();
  const key = (Object.keys(STATUS_MAP) as StatusType[]).find((candidate) => STATUS_MAP[candidate].label.toLowerCase() === normalized);
  return key ?? null;
};

const interactionIcon = (type: string): string =>
  type === 'call' ? 'bx:phone-call'
  : type === 'email' ? 'bx:envelope'
  : type === 'whatsapp' ? 'mdi:whatsapp'
  : 'bx:message-rounded-dots';

export const ActivityTimeline: React.FC<{ lead: MappedLeadData }> = ({ lead }) => {
  const timeline = useMemo<TimelineItem[]>(() => {
    const noteItems = (lead.isrNotes?.notes || []).map((note) => {
      const isStatusActivity = note.activity_type === 'status_change';
      const isMagnetSent = note.activity_type === 'magnet_sent';
      const presentation = isStatusActivity ? statusActivityPresentation(note) : null;
      const statusConfig = presentation?.status ? STATUS_MAP[presentation.status] : null;
      return {
        id: note.id,
        kind: isStatusActivity ? 'status' as const : isMagnetSent ? 'magnet' as const : 'note' as const,
        timestamp: note.created_at,
        title: presentation?.title || note.title || (isMagnetSent ? 'Magnet sent' : 'SDR note'),
        body: presentation?.body || note.body,
        author: note.created_by_name || note.created_by || 'SYSTEM',
        // Manual notes are HUMAN; status changes and magnet sends are SYSTEM.
        actor: isStatusActivity ? 'SYSTEM' as const : isMagnetSent ? 'SYSTEM' as const : 'HUMAN' as const,
        icon: note.icon || (isMagnetSent ? 'solar:gift-bold-duotone' : 'bx:notepad'),
        ...(statusConfig ? {
          statusTone: {
            color: statusConfig.color,
            background: statusConfig.bgColor,
            border: statusConfig.borderColor,
          },
        } : {}),
      };
    });
    const interactionItems = (lead.interactions || []).map((interaction: Interaction) => ({
      id: interaction.id,
      kind: 'interaction' as const,
      timestamp: interaction.timestamp,
      title: interaction.meta?.subject || interaction.type.replace(/_/g, ' '),
      body: interaction.content,
      author: interaction.participantName,
      // Outreach interactions are always AI AGENT actions.
      actor: 'AI AGENT' as const,
      icon: interactionIcon(interaction.type),
    }));
    return [...noteItems, ...interactionItems].sort((a, b) => {
      const aTime = Number.isNaN(Date.parse(a.timestamp)) ? 0 : Date.parse(a.timestamp);
      const bTime = Number.isNaN(Date.parse(b.timestamp)) ? 0 : Date.parse(b.timestamp);
      return bTime - aTime;
    });
  }, [lead.isrNotes, lead.interactions]);

  return (
    <div className="space-y-0">
      {timeline.map((item) => (
        <article key={`${item.kind}-${item.id}`} className="relative grid grid-cols-[26px_minmax(0,1fr)] gap-2.5 pb-4 last:pb-0">
          <span aria-hidden="true" className="absolute bottom-0 left-[12px] top-7 w-px bg-white/[0.08]" />
          <span
            className={`relative z-10 flex h-6 w-6 items-center justify-center rounded-md border ${item.kind === 'status' && item.statusTone ? '' : item.kind === 'status' ? 'border-violet-300/20 bg-violet-400/10 text-violet-200' : item.kind === 'note' ? 'border-cyan-300/20 bg-cyan-400/10 text-cyan-200' : 'border-white/10 bg-[#11151d] text-white/45'}`}
            style={item.kind === 'status' && item.statusTone ? {
              color: item.statusTone.color,
              backgroundColor: item.statusTone.background,
              borderColor: item.statusTone.border,
            } : undefined}
          >
            <Icon icon={item.icon} className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
              <h3 className="text-[12px] font-semibold text-white/80">{item.title}</h3>
              <time className="text-[10px] tabular-nums text-white/35" dateTime={item.timestamp}>
                {item.timestamp ? new Date(item.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
              </time>
            </div>
            {item.author && (
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] text-white/35">{item.author}</span>
                <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[8px] font-black uppercase tracking-[0.1em] ${ACTOR_BADGE[item.actor].bg} ${ACTOR_BADGE[item.actor].border} ${ACTOR_BADGE[item.actor].color}`}>
                  <Icon icon={ACTOR_BADGE[item.actor].icon} className="h-2.5 w-2.5" aria-hidden="true" />
                  {item.actor}
                </span>
              </div>
            )}
            <p className="mt-1 whitespace-pre-wrap break-words text-[12px] leading-5 text-white/65">{item.body}</p>
          </div>
        </article>
      ))}
      {!timeline.length && (
        <p className="rounded-lg border border-dashed border-white/10 px-4 py-6 text-center text-[12px] text-white/35">
          No notes or activity yet — this is the first contact.
        </p>
      )}
    </div>
  );
};
